import { useId } from 'react';
import { useFormContext, useFormState, useWatch } from 'react-hook-form';
import { DashboardPageFormField } from '@shigo-ui/dashboard-ui';
import {
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Textarea,
} from '@shigo-ui/ui';

import { dashboardT } from '../../../../../lib/messages';
import { applyCreativeBannerSize, type CreativeFormValue } from '../../creativeFormSchema';
import {
  creativeBannerSizes,
  CreativeFileKind,
  CreativeSubType,
  findCreativeBannerSize,
} from '../../model';
import { CreativeFileField } from './CreativeFileField';

export function CreativeDisplayFields({ editMode }: { editMode: boolean }) {
  const form = useFormContext<CreativeFormValue>();
  const { errors } = useFormState({ control: form.control });
  const subType = useWatch({ control: form.control, name: 'subType' });
  const bannerSize = useWatch({ control: form.control, name: 'bannerSize' });
  const file = useWatch({ control: form.control, name: 'file' });
  const subTypeId = useId();
  const bannerSizeId = useId();
  const contentId = useId();
  const clickThroughUrlId = useId();

  return (
    <>
      <DashboardPageFormField label={dashboardT('creatives.subType')} htmlFor={subTypeId}>
        <Select
          disabled={editMode}
          value={subType}
          onValueChange={(value) => {
            form.setValue('subType', value as CreativeSubType, { shouldDirty: true });
          }}
        >
          <SelectTrigger id={subTypeId}>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value={CreativeSubType.Image}>{dashboardT('creatives.image')}</SelectItem>
            <SelectItem value={CreativeSubType.Html}>{dashboardT('creatives.html')}</SelectItem>
          </SelectContent>
        </Select>
      </DashboardPageFormField>
      <DashboardPageFormField
        label={dashboardT('creatives.bannerSize')}
        htmlFor={bannerSizeId}
        required={!editMode}
        error={errors.bannerSize?.message}
      >
        <Select
          disabled={editMode}
          value={bannerSize}
          onValueChange={(nextBannerSize) => {
            const next = applyCreativeBannerSize(form.getValues(), nextBannerSize);
            form.setValue('bannerSize', next.bannerSize, {
              shouldDirty: true,
              shouldValidate: true,
            });
            form.setValue('width', next.width, { shouldDirty: true });
            form.setValue('height', next.height, { shouldDirty: true });
          }}
        >
          <SelectTrigger id={bannerSizeId} aria-required={!editMode}>
            <SelectValue placeholder={dashboardT('creatives.bannerSize')} />
          </SelectTrigger>
          <SelectContent>
            {creativeBannerSizes.map((size) => (
              <SelectItem key={size.key} value={size.key}>
                {size.key}
              </SelectItem>
            ))}
            {bannerSize && !findCreativeBannerSize(bannerSize) ? (
              <SelectItem value={bannerSize}>{bannerSize}</SelectItem>
            ) : null}
          </SelectContent>
        </Select>
      </DashboardPageFormField>
      {subType === CreativeSubType.Html ? (
        <DashboardPageFormField
          label={dashboardT('creatives.content')}
          htmlFor={contentId}
          required
          error={errors.content?.message}
        >
          <Textarea
            id={contentId}
            required
            className="min-h-56 font-mono"
            {...form.register('content')}
          />
        </DashboardPageFormField>
      ) : (
        <>
          <DashboardPageFormField
            label={dashboardT('creatives.clickThroughUrl')}
            required
            htmlFor={clickThroughUrlId}
            error={errors.clickThroughUrl?.message}
          >
            <Input
              id={clickThroughUrlId}
              type="url"
              required
              {...form.register('clickThroughUrl')}
            />
          </DashboardPageFormField>
          {!editMode ? (
            <CreativeFileField
              kind={CreativeFileKind.Image}
              file={file}
              error={errors.file?.message}
              onFileChange={(value) => {
                form.setValue('file', value, { shouldDirty: true, shouldValidate: true });
              }}
            />
          ) : null}
        </>
      )}
    </>
  );
}
